import { Loader2, Trash2, MessageCircle, UserCheck, MapPin, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateTimeInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { localeTag } from "@/i18n";
import { toInputDateTime } from "@/lib/format";
import { PALETTE, DEFAULT_COLOR } from "@/components/agenda/palette";

const ColorPicker = ({ value, autoColor, onChange, simple }) => (
  <div className="space-y-2">
    <Label>Colore</Label>
    <div className="flex flex-wrap items-center gap-1.5" data-testid="appointment-color-picker">
      <button type="button" onClick={() => onChange("")} title={simple ? "Automatico (blu appuntamenti)" : "Automatico (blu sopralluoghi)"}
        className={`h-7 px-2.5 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-colors ${!value ? "border-foreground bg-secondary" : "border-border hover:bg-secondary"}`}
        data-testid="appointment-color-auto">
        <Wand2 className="w-3.5 h-3.5" /> Auto <span className="w-3 h-3 rounded-full" style={{ background: autoColor }} />
      </button>
      {PALETTE.map((c) => (
        <button key={c} type="button" onClick={() => onChange(c)} style={{ background: c }}
          className={`w-7 h-7 rounded-full transition-transform hover:scale-110 ${value === c ? "ring-2 ring-offset-2 ring-offset-background ring-foreground" : ""}`}
          data-testid={`appointment-color-${c.slice(1)}`} />
      ))}
    </div>
  </div>
);

const JuniorDetail = ({ form, onClose }) => (
  <div className="space-y-4" data-testid="junior-appointment-detail">
    <div><div className="text-xs text-muted-foreground">Titolo</div><div className="font-semibold">{form.titolo || "\u2014"}</div></div>
    <div className="grid sm:grid-cols-2 gap-4">
      <div><div className="text-xs text-muted-foreground">Cliente</div><div className="font-medium">{form.cliente_nome || "\u2014"}</div></div>
      <div><div className="text-xs text-muted-foreground">Data e orario</div><div className="font-medium">{form.data ? new Date(form.data).toLocaleString(localeTag(), { dateStyle: "medium", timeStyle: "short" }) : "\u2014"}{form.data_fine ? ` \u2013 ${new Date(form.data_fine).toLocaleTimeString(localeTag(), { hour: "2-digit", minute: "2-digit" })}` : ""}</div></div>
    </div>
    <div><div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Luogo</div><div className="font-medium">{form.luogo || "\u2014"}</div></div>
    {form.note && <div><div className="text-xs text-muted-foreground">Note</div><div className="text-sm whitespace-pre-wrap">{form.note}</div></div>}
    <DialogFooter><Button onClick={onClose} data-testid="junior-detail-close">Chiudi</Button></DialogFooter>
  </div>
);

export const AppointmentDialog = ({ open, onClose, form, setForm, editId, isJunior, isAdmin, contacts, collabUsers, collabColors, collaboratori, onSave, onDelete, onShare, saving, simple = false }) => {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // Cambiando l'inizio, la fine si sposta insieme mantenendo la stessa durata (1 ora se non valida).
  const setStart = (e) => {
    const v = e.target.value;
    setForm((f) => {
      const s0 = new Date(f.data).getTime(), e0 = new Date(f.data_fine).getTime(), s1 = new Date(v).getTime();
      const dur = Number.isFinite(s0) && Number.isFinite(e0) && e0 > s0 ? e0 - s0 : 3600000;
      return { ...f, data: v, data_fine: Number.isFinite(s1) ? toInputDateTime(new Date(s1 + dur).toISOString()) : f.data_fine };
    });
  };
  const endBeforeStart = form.data && form.data_fine && new Date(form.data_fine) <= new Date(form.data);
  const handleContact = (id) => setForm((f) => ({ ...f, contact_id: id, cliente_nome: contacts.find((x) => x.id === id)?.ragione_sociale || "" }));
  const handleAssign = (id) => {
    const u = collabUsers.find((x) => x.id === id);
    setForm((f) => ({ ...f, assigned_to: id === "none" ? "" : id, assigned_nome: id === "none" ? "" : u?.name || u?.email || "" }));
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="appointment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isJunior ? "Dettaglio sopralluogo" : editId ? "Modifica appuntamento" : "Nuovo appuntamento"}</DialogTitle>
          <DialogDescription className="sr-only">Dati dell'appuntamento</DialogDescription>
        </DialogHeader>
        {isJunior ? <JuniorDetail form={form} onClose={onClose} /> : (
          <>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Titolo *</Label><Input value={form.titolo} onChange={set("titolo")} placeholder={simple ? "Es. Incontro con il cliente" : "Es. Sopralluogo cantiere"} data-testid="appointment-title-input" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2 sm:col-span-2">
                  <Label>Cliente</Label>
                  <Select value={form.contact_id || ""} onValueChange={handleContact}>
                    <SelectTrigger data-testid="appointment-contact-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Dalle *</Label><DateTimeInput value={form.data} onChange={setStart} data-testid="appointment-date-input" /></div>
                <div className="space-y-2"><Label>Alle (fine)</Label><DateTimeInput value={form.data_fine} onChange={set("data_fine")} data-testid="appointment-endtime-input" />
                  {endBeforeStart && <p className="text-sm text-destructive" data-testid="appointment-end-warning">La fine è prima dell'inizio: verrà impostata 1 ora dopo l'inizio.</p>}
                </div>
              </div>
              <div className="space-y-2"><Label>Luogo</Label><Input value={form.luogo} onChange={set("luogo")} data-testid="appointment-place-input" /></div>
              <div className="space-y-2"><Label>Note</Label><Textarea value={form.note} onChange={set("note")} rows={2} data-testid="appointment-notes-input" /></div>
              <ColorPicker value={form.colore} autoColor={DEFAULT_COLOR} simple={simple} onChange={(c) => setForm((f) => ({ ...f, colore: c }))} />
              {isAdmin && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><UserCheck className="w-4 h-4 text-primary" /> Assegna a collaboratore</Label>
                  <Select value={form.assigned_to || "none"} onValueChange={handleAssign}>
                    <SelectTrigger data-testid="appointment-assign-select"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuno (visibile solo a te)</SelectItem>
                      {collabUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id} data-testid={`appointment-assign-option-${u.id}`}>{u.name || u.email} {u.role === "junior" ? "· Junior" : "· Collab."}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2 border-t border-border pt-4">
                <Label className="flex items-center gap-1.5"><MessageCircle className="w-4 h-4 text-emerald-600" /> Condividi su WhatsApp a…</Label>
                {collaboratori.length ? (
                  <div className="flex flex-wrap gap-2" data-testid="appointment-share-list">
                    {collaboratori.map((c, i) => (
                      <Button key={i} type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => onShare(c)} data-testid={`appointment-share-${i}`}>
                        <MessageCircle className="w-4 h-4 text-emerald-600" /> {c.nome || c.telefono}
                      </Button>
                    ))}
                  </div>
                ) : <p className="text-xs text-muted-foreground">Nessun collaboratore salvato. Aggiungi i numeri in Impostazioni → Collaboratori (WhatsApp).</p>}
              </div>
            </div>
            <DialogFooter className="gap-2 sm:justify-between">
              {editId ? (
                <Button variant="outline" className="text-destructive hover:text-destructive" onClick={() => onDelete(editId)} data-testid="appointment-delete-button"><Trash2 className="w-4 h-4 mr-1.5" /> Elimina</Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>Annulla</Button>
                <Button onClick={onSave} disabled={saving} data-testid="appointment-save-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}</Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentDialog;
